<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <div class="page-header-with-filters">
      <d-row
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">
            {{ $t('indentRegistry.title') }}
          </span>
          <h3 class="page-title">
            {{ $t('indentRegistry.productsRegistry') }}
          </h3>
        </d-col>
        <d-col
          col
          sm="6"
          class="text-right text-sm-right d-flex justify-content-end align-items-end">
          <d-button
            @click.prevent="exportCustomers"
            theme="secondary"
            class="ml-2"
            :disabled="exportInProgress">
            <i
              v-if="!exportInProgress"
              class="material-icons">
              cloud_download
            </i>
            <div
              v-if="exportInProgress"
              class="spinner-border spinner-border-sm"
              role="status"></div>
            {{ $t('indentRegistry.exportRegistry') }}
          </d-button>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'indent-registry'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table
        v-if="allDataLoaded"
        class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('indentRegistry.registry') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('indentRegistry.order') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('indentRegistry.product') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('indentRegistry.productAdditionalData') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 450px;">{{ $t('indentRegistry.additionalInfo') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) of items"
            :key="'item-indent-registry-' + index">
            <td class="align-middle">
              {{ item.indent_registry }}
            </td>

            <td class="align-middle">
              <router-link :to="'/zamowienia-indent/edycja/' +  item.order_id">
                {{ item.order_name }}
              </router-link><br>
              <template v-if="item.customer_order_name">
                {{ $t('indentRegistry.customerOrderName') }}:<br> <span class="text-primary">{{ item.customer_order_name }}</span><br>
              </template>
              <template v-if="item.customer_order_date">
                {{ $t('indentRegistry.customerOrderDate') }}:<br> <span class="text-primary">{{ item.customer_order_date }}</span>
              </template>
            </td>

            <td class="align-middle">
              {{ item.code }} ({{ item.quantity }} {{ $t('ui.piecesAbbr') }})<br>
              <small>
                {{ item.base_product_name }} ({{ item.base_product_code }})
              </small>
            </td>

            <td class="align-middle">
              {{ $t('indentRegistry.cnCode') }}: <span class="text-primary">{{ item.cn_code }}</span><br>
              {{ $t('indentRegistry.customerDesignName') }}: <span class="text-primary">{{ item.customer_design_name }}</span><br>
            </td>

            <td class="align-middle">
              <a
                v-if="!item.detailsVisible && (item.service_obtaining_at || item.sample_production_days || item.service_accepted_at || item.freight_type || item.shipper_date || item.original_shipment_date || item.timeframe_production)"
                href="#"
                @click.prevent="toggleDetails(item, true)"
                class="d-block">
                {{ $t('indentRegistry.showDetails') }}
              </a>

              <template v-if="item.detailsVisible">
                <template v-if="item.service_obtaining_at">
                  {{ $t('indentRegistry.serviceObtainingAt') }}: <span class="text-primary">{{ item.service_obtaining_at }}</span><br>
                </template>

                <template v-if="item.sample_production_days">
                  {{ $t('indentRegistry.sampleProductionDays') }}: <span class="text-primary">{{ item.sample_production_days }}</span><br>
                </template>

                <template v-if="item.service_accepted_at">
                  {{ $t('indentRegistry.serviceAcceptedAt') }}: <span class="text-primary">{{ item.service_accepted_at }}</span><br>
                </template>

                <template v-if="item.freight_type">
                  {{ $t('indentRegistry.freightType') }}: <span class="text-primary">{{ item.freight_type }}</span><br>
                </template>

                <template v-if="item.shipper_date">
                  {{ $t('indentRegistry.shipperDate') }}: <span class="text-primary">{{ item.shipper_date }}</span><br>
                </template>

                <template v-if="item.original_shipment_date">
                  {{ $t('indentRegistry.originalShipmentDate') }}: <span class="text-primary">{{ item.original_shipment_date }}</span><br>
                </template>

                <template v-if="item.timeframe_production">
                  {{ $t('indentRegistry.timeframeProduction') }}: <span class="text-primary">{{ item.timeframe_production }}</span>
                </template>

                <template v-if="item.price">
                  {{ $t('indentRegistry.sellPrice') }}: <span class="text-primary">{{ Number(item.price * item.quantity).toFixed(2) }} {{ item.currency_code }}</span><br>
                  {{ $t('indentRegistry.unitSellPrice') }}: <span class="text-primary">{{ Number(item.price).toFixed(2) }} {{ item.currency_code }}</span>
                </template>
              </template>

              <a
                v-if="item.detailsVisible"
                href="#"
                class="d-block"
                @click.prevent="toggleDetails(item, false)">
                {{ $t('indentRegistry.hideDetails') }}
              </a>
            </td>
          </tr>
          <tr v-if="items.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('indentRegistry.noItemsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded && !loadError"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';
import Vue from 'vue';

export default {
  name: 'indent-registry',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    indentRegistryOptions () {
      return [
        {
          text: 'HSE',
          value: 'HSE'
        },
        {
          text: 'HPL',
          value: 'HPL'
        },
        {
          text: 'HBE',
          value: 'HBE'
        },
        {
          text: 'STOCK',
          value: 'STOCK'
        },
        {
          text: 'HGER',
          value: 'HGER'
        }
      ];
    },
    filtersConfig () {
      return [
        {
          key: 'order_name',
          apiKey: 'order_name',
          label: this.$t('ui.orderName'),
          placeholder: this.$t('indentRegistry.enterOrderName'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'indent_registry',
          apiKey: 'indent_registry',
          label: this.$t('indentRegistry.registry'),
          placeholder: this.$t('indentRegistry.selectRegistry'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.indentRegistryOptions
        }
      ];
    }
  },
  data () {
    return {
      items: [],
      loaded: false,
      loadError: false,
      exportInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {},
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/orders/indent/items?page=' + this.currentPage,
        listField: 'items',
        params: {
          where
        }
      });
    },
    toggleDetails (item, value) {
      Vue.set(item, 'detailsVisible', value);
    },
    exportCustomers () {
      this.exportInProgress = true;
      let exportParams = {
        where: cloneDeep(this.apiQueryPayload) || {}
      };

      this.$http.post('/orders/indent/export', exportParams, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('indentRegistry.indentRegistryLowerCase') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.exportErrorTitle'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    }
  }
};
</script>
